import React from "react";
import IconifyIcon from "../IconifyIcon";
import { useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import { AccessData } from "../../api/models/access-model";
import dayjs from "dayjs";
import ImageModal from "../Modals/ImageModal";
import {
  getAccessTransactionPhotoUrl,
  toAbsoluteMediaUrl,
} from "../../utils/getAccessTransactionPhotoUrl";

interface InvitationCardGuardProps {
  access: AccessData | null;
  style?: React.CSSProperties;
  setShowAccessDetails: (showAccessDetails: boolean) => void;
  setSelectedAccessId: (selectedAccessId: string) => void;
}

const AccessesCard = ({
  access,
  style,
  setShowAccessDetails,
  setSelectedAccessId,
}: InvitationCardGuardProps) => {
  const labels = useAppSelector(selectLabels);

  const [selectedPhoto, setSelectedPhoto] = React.useState<{
    type: string;
    url: string;
  } | null>(null);
  const [showPictureModal, setShowPictureModal] =
    React.useState<boolean>(false);

  const photos: { type: string; url: string }[] = React.useMemo(() => {
    return (
      access?.entrance_transaction?.access_transaction_photos?.map((item) => {
        return {
          type: item?.type, //photo id | plates | visitor | equipment
          url: getAccessTransactionPhotoUrl(item),
        };
      }) || []
    );
  }, [access?.entrance_transaction?.access_transaction_photos]);

  React.useEffect(() => {
    if (photos && photos.length > 0) {
      const photoId =
        photos.filter((item) => item?.type === "photo id")[0] || null;
      const photoPlates =
        photos.filter((item) => item?.type === "plates")[0] || null;
      const photoVisitor =
        photos.filter((item) => item?.type === "visitor")[0] || null;
      const photoEquipment =
        photos.filter((item) => item?.type === "equipment")[0] || null;

      if (photoId) {
        setSelectedPhoto(photoId);
      } else if (photoPlates) {
        setSelectedPhoto(photoPlates);
      } else if (photoVisitor) {
        setSelectedPhoto(photoVisitor);
      } else if (photoEquipment) {
        setSelectedPhoto(photoEquipment);
      }
    }
  }, [photos]);

  const imageToShow = React.useMemo(
    () => toAbsoluteMediaUrl(selectedPhoto?.url),
    [selectedPhoto],
  );

  return (
    <div
      className="card border-box flex flex-dir-col"
      style={style}
      onClick={() => {
        if (access?.documentId) {
          setShowAccessDetails(true);
          setSelectedAccessId(access?.documentId);
        }
      }}
    >
      <ImageModal
        showModal={showPictureModal}
        setShowModal={setShowPictureModal}
        // srcImage={`${selectedPhoto?.url}`}
        srcImage={imageToShow}
        imageType={`${selectedPhoto?.type}`}
        visitorName={`${access?.visitor}`}
      />
      <div className="w-100 border-box p-4 flex">
        <div className="w-85">
          <div className="w-100 border-box flex align-items-center justify-content-between">
            <div className="w-100 flex align-items-center">
              <p className="font-16 font-w-700 subtitle-color">
                {access?.visitor}
              </p>
            </div>
          </div>
          <div className="w-100 border-box flex align-items-center mt-5">
            {photos && photos.length > 0 && selectedPhoto && (
              <div
                className="flex align-items-center"
                style={{
                  minWidth: 60,
                  width: 60,
                  height: 60,
                  backgroundColor: "black",
                  borderRadius: 4,
                  marginRight: 8,
                  overflow: "hidden",
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowPictureModal(true);
                }}
              >
                <img
                  // src={selectedPhoto?.url}
                  src={imageToShow}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            )}
            <div style={{ width: "calc(100% - 40px)" }}>
              <p className="font-14 subtitle-color">{access?.user}</p>
              <p className="font-12 subtitle-color mt-2">{access?.unit}</p>
            </div>
          </div>
        </div>
        <div className="w-15 flex justify-content-end">
          {access?.entrance_transaction?.access_key_id ? (
            <>
              <IconifyIcon icon={"arcticons:qr"} fontSize={30} />
            </>
          ) : (
            <>
              <IconifyIcon
                icon={"arcticons:simple-clipboard-editor"}
                fontSize={30}
              />
            </>
          )}
        </div>
      </div>
      {photos && photos.length > 0 && (
        <div className="w-100 border-box pl-4 pr-4 pb-4 flex align-items-center gap-2">
          {photos.map((item, index) => (
            <div
              key={`${item?.type}-${index}`}
              className={
                selectedPhoto?.type === item?.type
                  ? "photo-button-preview-selected"
                  : "photo-button-preview"
              }
              onClick={(event) => {
                event.stopPropagation();
                setSelectedPhoto(item);
              }}
            >
              <IconifyIcon
                icon={
                  item?.type === "photo id"
                    ? "fa-regular:id-card"
                    : item?.type === "plates"
                      ? "fa-solid:car-alt"
                      : item?.type === "visitor"
                        ? "fa-solid:user"
                        : "fa-solid:laptop"
                }
              />
            </div>
          ))}
        </div>
      )}
      {!access?.authorized && (
        <div className="w-100 border-box flex align-items-center justify-content-start gap-2 px-4 border-box">
          <IconifyIcon icon={"entypo:block"} style={{ color: "#b71c1c" }} />
          <p className="font-14 font-w-500" style={{ color: "#b71c1c" }}>
            {labels.not_authorized}
          </p>
        </div>
      )}
      <div className="w-100 border-box pl-4 pr-4 pb-4 pt-2">
        <>
          {access?.entrance_transaction != null && (
            <div
              style={{
                border: "1px #dfdfdf solid",
                borderRadius: 4,
              }}
            >
              {access?.entrance_transaction?.gate?.access_point?.name && (
                <div
                  className="w-100 flex align-items-center justify-content-end gap-4 p-2 border-box"
                  style={{
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  <p className="font-12 subtitle-color">
                    {access?.entrance_transaction?.gate?.access_point?.name}
                  </p>
                </div>
              )}
              <div className="w-100 flex align-items-center gap-4 p-2 border-box">
                <IconifyIcon icon={"entypo:login"} className="title-color" />
                <p className="font-14 subtitle-color">
                  {labels.entrance}:{" "}
                  <span className="font-w-700">
                    {dayjs(access?.entrance_transaction?.datetime).format(
                      "DD/MMM hh:mm",
                    )}
                  </span>
                </p>
              </div>
            </div>
          )}
          {access?.exit_transaction != null && (
            <div
              className="mt-4"
              style={{
                border: "1px #dfdfdf solid",
                borderRadius: 4,
              }}
            >
              {access?.exit_transaction?.gate?.access_point?.name && (
                <div
                  className="w-100 flex align-items-center justify-content-end gap-4 p-2 border-box"
                  style={{
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  <p className="font-12 subtitle-color">
                    {access?.exit_transaction?.gate?.access_point?.name}
                  </p>
                </div>
              )}
              <div className="w-100 flex align-items-center gap-4 p-2 border-box">
                <IconifyIcon icon={"entypo:log-out"} className="title-color" />
                <p className="font-14 subtitle-color">
                  {labels.exit}:{" "}
                  <span className="font-w-700">
                    {dayjs(access?.exit_transaction?.datetime).format(
                      "DD/MMM hh:mm",
                    )}
                  </span>
                </p>
              </div>
            </div>
          )}
        </>
      </div>
    </div>
  );
};

export default AccessesCard;
